import React, { useState, useRef, useEffect } from "react";

const DownloadModal = ({ showModal, selectedFile, onClose, basePath }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const formRef = useRef({
        FullName: '',
        Email: '',
        Phone: '',
        Message: ''
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        formRef.current[name] = value;
        setError('');
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const endpoint = `${basePath}/doDownload`;

            const formData = new URLSearchParams();
            formData.append('FullName', formRef.current.FullName);
            formData.append('Email', formRef.current.Email);
            formData.append('Phone', formRef.current.Phone);
            formData.append('Message', formRef.current.Message);
            formData.append('ResourceTitle', selectedFile.resourceTitle);
            formData.append('FileURL', selectedFile.url);

            try {
                const formUrl = `${basePath}/DownloadForm`;
                const securityTokenResponse = await fetch(formUrl);
                if (securityTokenResponse.ok) {
                    const securityTokenHTML = await securityTokenResponse.text();
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(securityTokenHTML, 'text/html');
                    const securityTokenInput = doc.querySelector('input[name="SecurityID"]');

                    if (securityTokenInput && securityTokenInput.value) {
                        formData.append('SecurityID', securityTokenInput.value);
                    }
                }
            } catch (tokenError) {
                console.warn('Could not fetch security token:', tokenError);
            }

            const response = await fetch(endpoint, {
                method: 'POST',
                body: formData,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Accept': 'application/json'
                },
                credentials: 'same-origin'
            });

            if (response.ok) {
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    const data = await response.json();

                    if (data.success) {
                        setSuccess(true);
                    } else {
                        setError(data.error || 'Submission failed. Please try again.');
                    }
                } else {
                    const text = await response.text();
                    console.error('Non-JSON response:', text);
                    setError('Server returned an unexpected response format.');
                }
            } else {
                try {
                    const errorData = await response.json();
                    setError(errorData.error || `Server error (${response.status}). Please try again.`);
                } catch {
                    const errorText = await response.text();
                    console.error('Server error response:', errorText);
                    setError(`Server error (${response.status}). Please try again.`);
                }
            }
        } catch (error) {
            console.error('Network error:', error);
            setError('Network error. Please check your connection and try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleCloseModal = () => {
        formRef.current = {
            FullName: '',
            Email: '',
            Phone: '',
            Message: ''
        };
        setError('');
        setSuccess(false);
        onClose();
    };

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && showModal) {
                handleCloseModal();
            }
        };

        if (showModal) {
            document.addEventListener('keydown', handleEscape);
            document.body.classList.add('overflow-hidden');
        } else {
            document.body.classList.remove('overflow-hidden');
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.classList.remove('overflow-hidden');
        };
    }, [showModal]);

    if (!showModal || !selectedFile) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div
                className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                onClick={handleCloseModal}
            />

            <div className="relative min-h-screen flex items-center justify-center p-4">
                <div
                    className="relative bg-white rounded-lg shadow-xl max-w-lg w-full mt-8"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex items-center justify-between p-6 border-b border-gray-200">
                        <h3 className="text-xl font-semibold text-gray-900 m-0">
                            Download {selectedFile.resourceTitle}
                        </h3>
                        <button
                            className="text-gray-400 hover:text-gray-500 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                            onClick={handleCloseModal}
                            type="button"
                            aria-label="Close">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                        </button>
                    </div>

                    <div className="p-6">
                        {!success && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                                <p className="text-sm text-gray-600 mb-0">
                                    Please fill out this form to receive a download link.
                                </p>
                            </div>
                        )}

                        {success && (
                            <div className="bg-green-50 border border-green-200 text-green-700 px-5 py-2 rounded mb-4 text-sm">
                                <div className="flex items-start mt-4">
                                    <div>
                                        <p className="flex gap-2 font-semibold">
                                            <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                            Thank you!
                                        </p>
                                        <p className="mt-1">An email has been sent to <strong>{formRef.current.Email}</strong> with a download link for the resource. The link will expire in 24 hours.</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {error && !success && (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 text-sm">
                                <div className="flex items-center">
                                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                    {error}
                                </div>
                            </div>
                        )}

                        {!success && (
                            <form onSubmit={handleFormSubmit} className="space-y-4">
                                <div>
                                    <label htmlFor="FullName" className="block text-sm font-medium text-gray-700 mb-1">
                                        Full Name *
                                    </label>
                                    <input
                                        type="text"
                                        id="FullName"
                                        name="FullName"
                                        defaultValue={formRef.current.FullName}
                                        onChange={handleInputChange}
                                        required
                                        disabled={loading}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                        placeholder="Enter your full name"
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="Email" className="block text-sm font-medium text-gray-700 mb-1">
                                            Email *
                                        </label>
                                        <input
                                            type="email"
                                            id="Email"
                                            name="Email"
                                            defaultValue={formRef.current.Email}
                                            onChange={handleInputChange}
                                            required
                                            disabled={loading}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                            placeholder="Enter your email"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="Phone" className="block text-sm font-medium text-gray-700 mb-1">
                                            Phone
                                        </label>
                                        <input
                                            type="tel"
                                            id="Phone"
                                            name="Phone"
                                            defaultValue={formRef.current.Phone}
                                            onChange={handleInputChange}
                                            disabled={loading}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                            placeholder="Enter your phone number"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="Message" className="block text-sm font-medium text-gray-700 mb-1">
                                        Message
                                    </label>
                                    <textarea
                                        id="Message"
                                        name="Message"
                                        defaultValue={formRef.current.Message}
                                        onChange={handleInputChange}
                                        disabled={loading}
                                        rows="3"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                        placeholder="Any additional information..."
                                    />
                                </div>

                                <div className="pt-0">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="btn w-full"
                                    >
                                        {loading ? (
                                            <span className="flex items-center justify-center">
                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Sending...
                                            </span>
                                        ) : 'Send Download Link'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DownloadModal;